import { useState } from "react";
import { Crown, Shield, User, UserPlus, Mail, Link2, Bell, BellOff, X, LogOut, ChevronDown, Check } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger, DropdownMenuLabel } from "@/components/ui/dropdown-menu";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { validateEmail } from "@/lib/emailValidation";

const COUNTRY_CODES = [
  { code: "+91", flag: "🇮🇳" },
  { code: "+1", flag: "🇺🇸" },
  { code: "+44", flag: "🇬🇧" },
  { code: "+971", flag: "🇦🇪" },
  { code: "+65", flag: "🇸🇬" },
  { code: "+61", flag: "🇦🇺" },
];

interface DiscussionActionBarProps {
  entityType: 'event' | 'purchase';
  entityId: string;
  entityTitle: string;
  role: 'organizer' | 'admin' | 'participant';
  themeColor: 'pink' | 'purple';
  inviteLinkEnabled?: boolean;
  inviteLinkToken?: string;
  inviteLinkApprovalMode?: 'auto' | 'manual';
  userEmail: string;
  reminderHoursBefore?: number[];
  onRefresh: () => void;
  onLeaveSuccess?: () => void;
}

export function DiscussionActionBar({
  entityType,
  entityId,
  entityTitle,
  role,
  themeColor,
  inviteLinkEnabled,
  inviteLinkToken,
  inviteLinkApprovalMode,
  userEmail,
  reminderHoursBefore = [],
  onRefresh,
  onLeaveSuccess,
}: DiscussionActionBarProps) {
  const isOrganizer = role === 'organizer';
  const isAdmin = role === 'admin';
  const canCancel = isOrganizer || isAdmin;
  
  // Invite dialog state
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteName, setInviteName] = useState("");
  const [invitePhone, setInvitePhone] = useState("");
  const [phoneCountryCode, setPhoneCountryCode] = useState("+91");
  const [inviting, setInviting] = useState(false);
  
  // Notify dialog state
  const [showNotifyDialog, setShowNotifyDialog] = useState(false);
  const [selectedReminder, setSelectedReminder] = useState<number | null>(null);
  const [isMuted, setIsMuted] = useState(false);
  const [savingNotify, setSavingNotify] = useState(false);
  
  // Cancel/Leave dialog state
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showLeaveDialog, setShowLeaveDialog] = useState(false);
  const [cancelMessage, setCancelMessage] = useState("");
  const [cancelling, setCancelling] = useState(false);
  const [leaving, setLeaving] = useState(false);
  
  const themeClasses = themeColor === 'pink' 
    ? {
        roleBadge: "bg-pink-500/10 text-pink-700 border-pink-500/20",
        adminBadge: "bg-purple-500/10 text-purple-700 border-purple-500/20",
        participantBadge: "bg-muted text-muted-foreground border-border",
      }
    : {
        roleBadge: "bg-purple-500/10 text-purple-700 border-purple-500/20",
        adminBadge: "bg-emerald-500/10 text-emerald-700 border-emerald-500/20",
        participantBadge: "bg-muted text-muted-foreground border-border",
      };

  const handleCopyLink = async () => {
    if (!inviteLinkToken) return;
    const url = `${window.location.origin}/join/${entityType}/${inviteLinkToken}`;
    await navigator.clipboard.writeText(url);
    toast.success("Invite link copied!", {
      description: inviteLinkApprovalMode === 'manual' 
        ? "New members will need your approval" 
        : "New members will be auto-approved"
    });
  };

  const handleInviteByEmail = async () => {
    if (!inviteEmail.trim()) {
      toast.error("Please enter an email address");
      return;
    }
    
    const emailValidation = validateEmail(inviteEmail);
    if (!emailValidation.valid) {
      toast.error(emailValidation.error || "Invalid email");
      return;
    }
    
    setInviting(true);
    try {
      const accessToken = crypto.randomUUID();
      const phone = invitePhone ? `${phoneCountryCode}${invitePhone.replace(/\D/g, '')}` : null;
      
      if (entityType === 'event') {
        // Check if already invited
        const { data: existing } = await supabase
          .from('event_invitees')
          .select('id')
          .eq('reminder_id', entityId)
          .eq('email', inviteEmail.toLowerCase())
          .maybeSingle();
        
        if (existing) {
          toast.error("This person is already invited");
          setInviting(false);
          return;
        }
        
        const { error } = await supabase
          .from('event_invitees')
          .insert({
            reminder_id: entityId,
            email: inviteEmail.toLowerCase(),
            name: inviteName || null,
            phone,
            invited_by: userEmail,
            status: 'active',
            role: 'participant' as const,
            access_token: accessToken,
          });
        
        if (error) throw error;
      } else {
        // Check if already invited
        const { data: existing } = await supabase
          .from('group_purchase_participants')
          .select('id')
          .eq('purchase_id', entityId)
          .eq('email', inviteEmail.toLowerCase())
          .maybeSingle();
        
        if (existing) {
          toast.error("This person is already invited");
          setInviting(false);
          return;
        }
        
        const { error } = await supabase
          .from('group_purchase_participants')
          .insert({
            purchase_id: entityId,
            email: inviteEmail.toLowerCase(),
            name: inviteName || null,
            invited_by: userEmail,
            status: 'active',
            role: 'participant' as const,
            access_token: accessToken,
          });
        
        if (error) throw error;
      }
      
      // Send invitation email
      const functionName = entityType === 'event' ? 'send-event-invitation' : 'send-group-purchase-invitation';
      await supabase.functions.invoke(functionName, {
        body: entityType === 'event' 
          ? { reminderId: entityId, inviteeEmail: inviteEmail.toLowerCase() }
          : { purchaseId: entityId, participantEmail: inviteEmail.toLowerCase() }
      });
      
      toast.success("Invitation sent!");
      setInviteEmail("");
      setInviteName("");
      setInvitePhone("");
      setShowInviteDialog(false);
      onRefresh();
    } catch (error: any) {
      console.error("Error inviting:", error);
      toast.error("Failed to send invitation");
    } finally {
      setInviting(false);
    }
  };

  const handleSaveNotification = async () => {
    setSavingNotify(true);
    try {
      const tableName = entityType === 'event' ? 'gift_reminders' : 'group_purchases';
      
      // Convert selected reminder days to hours
      let hoursArray: number[] = [];
      if (!isMuted && selectedReminder) {
        hoursArray = [selectedReminder * 24];
      }
      
      if (entityType === 'event') {
        const { error } = await supabase
          .from(tableName)
          .update({ reminder_hours_before: hoursArray })
          .eq('id', entityId);
        
        if (error) throw error;
      }
      
      toast.success(isMuted ? "Notifications muted" : `You'll be reminded ${selectedReminder} day${selectedReminder !== 1 ? 's' : ''} before`);
      setShowNotifyDialog(false);
      onRefresh();
    } catch (error) {
      console.error("Error saving notification:", error);
      toast.error("Failed to update notification settings");
    } finally {
      setSavingNotify(false);
    }
  };

  const handleCancel = async () => {
    setCancelling(true);
    try {
      const tableName = entityType === 'event' ? 'gift_reminders' : 'group_purchases';
      
      if (entityType === 'event') {
        // Call the cancel edge function for events
        const { error } = await supabase.functions.invoke('notify-event-cancelled', {
          body: { 
            reminderId: entityId, 
            reason: cancelMessage || undefined 
          }
        });
        
        if (error) throw error;
        
        // Delete the reminder
        await supabase.from('gift_reminders').delete().eq('id', entityId);
      } else {
        // Update purchase status
        const { error } = await supabase
          .from(tableName)
          .update({ 
            status: 'cancelled',
            completed_at: new Date().toISOString()
          })
          .eq('id', entityId);
        
        if (error) throw error;
      }
      
      toast.success(`${entityType === 'event' ? 'Event' : 'Purchase'} cancelled`);
      setShowCancelDialog(false);
      
      // Navigate away
      window.location.href = '/my-gifts';
    } catch (error) {
      console.error("Error cancelling:", error);
      toast.error("Failed to cancel");
    } finally {
      setCancelling(false);
    }
  };

  const handleLeave = async () => {
    setLeaving(true);
    try {
      if (entityType === 'event') {
        const { error } = await supabase
          .from('event_invitees')
          .update({ status: 'left' })
          .eq('reminder_id', entityId)
          .eq('email', userEmail.toLowerCase());
        
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('group_purchase_participants')
          .update({ status: 'left' })
          .eq('purchase_id', entityId)
          .eq('email', userEmail.toLowerCase());
        
        if (error) throw error;
      }
      
      toast.success(`You've left the ${entityType === 'event' ? 'event' : 'purchase'}`);
      setShowLeaveDialog(false);
      onLeaveSuccess?.();
    } catch (error) {
      console.error("Error leaving:", error);
      toast.error("Failed to leave");
    } finally {
      setLeaving(false);
    }
  };

  // Determine current notification status
  const currentReminderDays = reminderHoursBefore.length > 0 
    ? Math.round(reminderHoursBefore[0] / 24) 
    : null;

  return (
    <div className="flex flex-wrap items-center gap-2 mb-4">
      {/* Role Badge */}
      {isOrganizer ? (
        <Badge className={`${themeClasses.roleBadge} gap-1`}>
          <Crown className="w-3 h-3" />
          Organizer
        </Badge>
      ) : isAdmin ? (
        <Badge className={`${themeClasses.adminBadge} gap-1`}>
          <Shield className="w-3 h-3" />
          Admin
        </Badge>
      ) : (
        <Badge className={`${themeClasses.participantBadge} gap-1`} variant="outline">
          <User className="w-3 h-3" />
          Participant
        </Badge>
      )}

      {/* Invite Dropdown */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className="gap-2">
            <UserPlus className="w-4 h-4" />
            Invite
            <ChevronDown className="w-3 h-3" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-48 bg-background">
          <DropdownMenuItem onClick={() => setShowInviteDialog(true)}>
            <Mail className="w-4 h-4 mr-2" />
            Add by Email
          </DropdownMenuItem>
          {inviteLinkEnabled && inviteLinkToken ? (
            <DropdownMenuItem onClick={handleCopyLink}>
              <Link2 className="w-4 h-4 mr-2" />
              Copy Share Link
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem disabled className="text-muted-foreground">
              <Link2 className="w-4 h-4 mr-2" />
              Share Link (disabled)
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Notify Me Dropdown */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className="gap-2">
            {isMuted || !currentReminderDays ? (
              <>
                <BellOff className="w-4 h-4" />
                Muted
              </>
            ) : (
              <>
                <Bell className="w-4 h-4" />
                Notify me
              </>
            )}
            <ChevronDown className="w-3 h-3" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-56 bg-background">
          <DropdownMenuLabel className="text-xs text-muted-foreground">
            Email reminder before event
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          {[14, 7, 3, 1].map((days) => (
            <DropdownMenuItem 
              key={days}
              onClick={() => {
                setSelectedReminder(days);
                setIsMuted(false);
                setShowNotifyDialog(true);
              }}
              className="justify-between"
            >
              {days} day{days !== 1 ? 's' : ''} before
              {currentReminderDays === days && !isMuted && (
                <Check className="w-4 h-4 text-primary" />
              )}
            </DropdownMenuItem>
          ))}
          <DropdownMenuSeparator />
          <DropdownMenuItem 
            onClick={() => {
              setIsMuted(true);
              setSelectedReminder(null);
              setShowNotifyDialog(true);
            }}
            className="justify-between"
          >
            <span className="flex items-center gap-2">
              <BellOff className="w-4 h-4" />
              Mute notifications
            </span>
            {isMuted && <Check className="w-4 h-4 text-primary" />}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Cancel Event (for organizer/admin) or Leave (for participants) */}
      {canCancel ? (
        <Button 
          variant="outline" 
          size="sm" 
          className="gap-2 text-destructive border-destructive/30 hover:bg-destructive/10"
          onClick={() => setShowCancelDialog(true)}
        >
          <X className="w-4 h-4" />
          Cancel {entityType === 'event' ? 'Event' : 'Purchase'}
        </Button>
      ) : (
        <Button 
          variant="outline" 
          size="sm" 
          className="gap-2 text-muted-foreground hover:text-destructive hover:border-destructive/30"
          onClick={() => setShowLeaveDialog(true)}
        >
          <LogOut className="w-4 h-4" />
          Leave
        </Button>
      )}

      {/* Invite by Email Dialog */}
      <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Invite by Email</DialogTitle>
            <DialogDescription>
              Add someone to this {entityType === 'event' ? 'gift event' : 'group purchase'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Input
              type="email"
              placeholder="Email address *"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
            />
            <Input
              placeholder="Name (optional)"
              value={inviteName}
              onChange={(e) => setInviteName(e.target.value)}
            />
            {entityType === 'event' && (
              <div className="flex gap-2">
                <Select value={phoneCountryCode} onValueChange={setPhoneCountryCode}>
                  <SelectTrigger className="w-24">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-background">
                    {COUNTRY_CODES.map((c) => (
                      <SelectItem key={c.code} value={c.code}>
                        {c.flag} {c.code}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  placeholder="Phone (optional)"
                  value={invitePhone}
                  onChange={(e) => setInvitePhone(e.target.value)}
                  className="flex-1"
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInviteDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleInviteByEmail} disabled={inviting}>
              {inviting ? "Sending..." : "Send Invitation"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Notification Confirmation Dialog */}
      <Dialog open={showNotifyDialog} onOpenChange={setShowNotifyDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {isMuted ? "Mute Notifications?" : "Set Reminder"}
            </DialogTitle>
            <DialogDescription>
              {isMuted 
                ? "You won't receive email reminders for this event."
                : `You'll receive an email reminder ${selectedReminder} day${selectedReminder !== 1 ? 's' : ''} before the event.`
              }
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNotifyDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveNotification} disabled={savingNotify}>
              {savingNotify ? "Saving..." : "Confirm"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Event Dialog */}
      <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancel this {entityType === 'event' ? 'event' : 'purchase'}?</DialogTitle>
            <DialogDescription>
              All participants will be notified. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            placeholder="Add a message to participants (optional)"
            value={cancelMessage}
            onChange={(e) => setCancelMessage(e.target.value)}
            rows={3}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCancelDialog(false)}>
              Keep {entityType === 'event' ? 'Event' : 'Purchase'}
            </Button>
            <Button variant="destructive" onClick={handleCancel} disabled={cancelling}>
              {cancelling ? "Cancelling..." : `Cancel ${entityType === 'event' ? 'Event' : 'Purchase'}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Leave Dialog */}
      <Dialog open={showLeaveDialog} onOpenChange={setShowLeaveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Leave "{entityTitle}"?</DialogTitle>
            <DialogDescription>
              You will no longer receive updates and won't be able to participate in discussions.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLeaveDialog(false)}>
              Stay
            </Button>
            <Button variant="destructive" onClick={handleLeave} disabled={leaving}>
              {leaving ? "Leaving..." : "Leave"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
